import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ICartItem, RootState } from "../../../../../../models";
import {
    AdditionalService,
    ChangeScope,
    CheckoutSubscription,
    CustomerExistingSubscription,
} from "../../../../../../models/state";
import {
    calculateEffectivePrice,
    getSubscriptionTitleWithBreakageFee,
    getYearlyCommitmentDiscountLabel,
    isCommitmentActive,
    isSubscriptionActive,
} from "../../../utils";
import { hasCustomerLookupOrCreditCheckBeenPerformed } from "../../../state/selectors/customerSelectors";
import { RetailerSubscriptionAccordion } from "../../organisms/RetailerSubscriptionAccordion";
import { isZeroPlaySubscription } from "../../../../../../shared/subscriptionCodeUtils";
import PAGES from "../../../../../../constants/pages";
import { Box, Button, Card, Flex, Heading, HelperText, Icon, Text, Tooltip } from "@telia/teddy";
import moment from "moment";
import _capitalize from "lodash/capitalize";
import "./ExistingSubscriptionCard.less";

const DATE_FORMAT_DDMMYYYY_WITH_SEPARATION = (separator: string) => ["DD", "MM", "YYYY"].join(separator);

const normalizeValue = (value?: string): string => (value || "").replace(/\s+/g, " ").trim().toLowerCase();

const formatBirthDate = (value?: string): string => {
    if (!value) {
        return "Ikke oppgitt";
    }

    const parsed = moment(value, ["YYYY-MM-DD", "DD.MM.YYYY"], true);
    return parsed.isValid() ? parsed.format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION(".")) : "Ikke oppgitt";
};

const formatProductUserName = (firstName?: string, lastName?: string): string =>
    `${_capitalize(firstName || "")} ${_capitalize(lastName || "")}`.trim() || "Ikke oppgitt";

const formatProductUserAddress = (street?: string, postcode?: string, city?: string): string => {
    const postalLine = [postcode, city].filter(Boolean).join(" ");
    return [street, postalLine].filter(Boolean).join(", ") || "Ikke oppgitt";
};

const formatSimNumber = (value?: string): string => {
    if (!value) {
        return "Ikke oppgitt";
    }

    const digitsOnly = value.replace(/\D+/g, "");

    const simPrefix = digitsOnly.slice(0, 8);
    const simNumber = digitsOnly.slice(8);
    return `[ ${simPrefix} ] ${simNumber}`;
};

const formatServiceStatus = (status?: string): string => {
    switch (status) {
        case "ACTIVE":
            return "Aktiv";
        default:
            return status || "";
    }
};

const formatServiceMonthlyPrice = (monthlyPrice?: number): string =>
    monthlyPrice === undefined ? "" : `(${Math.round(monthlyPrice)},-/md.)`;

const getSubscriptionDisplayName = (sub: CustomerExistingSubscription, changedName?: string, isChanged?: boolean) => {
    if (isChanged && changedName) return `${sub.phoneNumber} - ${changedName}`;
    if (isZeroPlaySubscription(sub.offeringCode)) return sub.offeringName ?? undefined;
    if (sub.offeringName) return `${sub.phoneNumber} - ${sub.offeringName}`;
    return (
        <>
            {sub.phoneNumber} - <i>Utgått abo</i>
        </>
    );
};

const renderFieldDiff = (currentValue: string, nextValue?: string, dataMask = false) => {
    const hasChanged = !!nextValue && normalizeValue(currentValue) !== normalizeValue(nextValue);

    if (!hasChanged) {
        return dataMask ? <span data-di-mask>{currentValue}</span> : <>{currentValue}</>;
    }

    const oldNode = dataMask ? <span data-di-mask>{currentValue}</span> : <span>{currentValue}</span>;
    const newNode = dataMask ? <span data-di-mask>{nextValue}</span> : <span>{nextValue}</span>;

    return (
        <Flex align="center" gapX="100">
            {oldNode}
            <Icon name="arrow-right" size="sm" aria-hidden="true" focusable="false" />
            {newNode}
        </Flex>
    );
};

interface Props {
    changedSubscription?: CheckoutSubscription;
    changedSubscriptionCartItem?: ICartItem;
    familyDiscount?: boolean;
    handleChangeSubscription: (subscription: CustomerExistingSubscription, scope?: ChangeScope) => void;
    handleSelect: (id: string) => void;
    isChanged?: boolean;
    isChangeBlockedByAccountFull?: boolean;
    isBlockedDueToNaturallyFullAccount?: boolean;
    selectedSubscription: string | undefined;
    sub: CustomerExistingSubscription;
    subscriptionInfo: CustomerExistingSubscription | undefined;
    isCurrentlyAddingSubscription?: boolean;
    readOnly?: boolean;
}

const ExistingSubscriptionCard: React.FC<Props> = ({
    sub,
    selectedSubscription,
    handleSelect,
    handleChangeSubscription,
    subscriptionInfo,
    familyDiscount,
    isChanged,
    isChangeBlockedByAccountFull,
    isBlockedDueToNaturallyFullAccount,
    changedSubscription,
    changedSubscriptionCartItem,
    isCurrentlyAddingSubscription,
    readOnly,
}) => {
    const isActive = isSubscriptionActive(sub);
    const showActions = isActive && !readOnly;
    const yearlyCommitmentLabel = getYearlyCommitmentDiscountLabel(
        isChanged ? (changedSubscription?.code ?? sub.offeringCode) : sub.offeringCode,
    );
    const isSelected = selectedSubscription === sub.phoneNumber;
    const isCustomerDataFetched = useSelector((state: RootState) => hasCustomerLookupOrCreditCheckBeenPerformed(state));
    const [isServicesExpanded, setIsServicesExpanded] = useState(false);

    if (sub.isSecretNumber) {
        return (
            <RetailerSubscriptionAccordion
                id={`${sub.phoneNumber}-${sub.offeringCode ?? "unknown"}`}
                isActive={isActive}
                isSelectable={false}
                title={getSubscriptionTitleWithBreakageFee(sub, isCustomerDataFetched)}
                name={
                    <>
                        {sub.maskedPhoneNumber} (hemmelig nummer)
                        <br />
                        <i>Bruk manuelt skjema</i>
                    </>
                }
                price="n/a"
                priceSuffix=""
                yearlyCommitmentLabel={yearlyCommitmentLabel}
            />
        );
    }

    const { effectivePrice, priceStriketrough } = calculateEffectivePrice(sub, familyDiscount);

    const { effectivePrice: changedPrice, priceStriketrough: changedPriceStrikethrough } = changedSubscription
        ? calculateEffectivePrice(
              {
                  price: changedSubscription.price?.monthly?.amountSubscription ?? sub.price,
                  priceLines: changedSubscription.price?.monthlyLines?.amountSubscription ?? sub.priceLines,
                  offeringCode: changedSubscription.code,
              },
              familyDiscount,
          )
        : { effectivePrice: undefined, priceStriketrough: undefined };

    const changedProductUser = changedSubscription?.productUser;
    const currentUserName = formatProductUserName(subscriptionInfo?.firstName, subscriptionInfo?.lastName);
    const nextUserName = changedProductUser
        ? formatProductUserName(changedProductUser.firstName, changedProductUser.lastName)
        : undefined;
    const currentBirthDate = formatBirthDate(subscriptionInfo?.birthDate);
    const nextBirthDate = changedProductUser ? formatBirthDate(changedProductUser.birthDate) : undefined;
    const currentEmail = subscriptionInfo?.email || "Ikke oppgitt";
    const nextEmail = changedProductUser?.email || undefined;
    const currentAddress = subscriptionInfo?.address ? _capitalize(subscriptionInfo.address) : "Ikke oppgitt";
    const nextAddress = changedProductUser
        ? formatProductUserAddress(changedProductUser.street, changedProductUser.postcode, changedProductUser.city)
        : undefined;

    const changedSimItem = changedSubscriptionCartItem?.items?.find(
        (item) => item.type === "SIM" || item.type === "ESIM",
    );
    const changedExtraSims = changedSubscription?.changedExtraSims ?? [];
    const hasChangedMainSim = !!changedSimItem;
    const hasChangedSim = hasChangedMainSim || changedExtraSims.length > 0;
    const currentMainSim = formatSimNumber(subscriptionInfo?.mainSimcard);
    let nextMainSim: string | undefined;
    if (hasChangedMainSim) {
        nextMainSim =
            changedSubscription?.isSimFromStoreSelected && changedSubscription?.storeSimNumber
                ? formatSimNumber(changedSubscription.storeSimNumber)
                : "Nytt SIM-kort";
    }

    let currentSimType = "Ikke oppgitt";
    if (subscriptionInfo?.mainSimcardIsEsim === true) {
        currentSimType = "eSIM";
    } else if (subscriptionInfo?.mainSimcardIsEsim === false) {
        currentSimType = "Fysisk SIM";
    }

    let nextSimType: string | undefined;
    if (hasChangedMainSim) {
        nextSimType = changedSubscription?.isEsimSelected || changedSimItem?.type === "ESIM" ? "eSIM" : "Fysisk SIM";
    }
    const hasSubscriptionChanged =
        changedSubscription?.changeType ||
        (sub.offeringCode && changedSubscription?.code && sub.offeringCode !== changedSubscription?.code);
    const hasFieldChanged = (current: string, next?: string) =>
        !!next && normalizeValue(current) !== normalizeValue(next);
    const hasUserChanged =
        !!isChanged &&
        (hasFieldChanged(currentUserName, nextUserName) ||
            hasFieldChanged(currentBirthDate, nextBirthDate) ||
            hasFieldChanged(currentEmail, nextEmail) ||
            hasFieldChanged(currentAddress, nextAddress));
    const hasServicesChanged = changedSubscription?.existingSubscriptionUpdateScope === "vas";

    const displayName = getSubscriptionDisplayName(sub, changedSubscription?.name, isChanged);
    const accountFullTooltipMessage = isBlockedDueToNaturallyFullAccount
        ? "Valgt fakturanummer er fullt. Velg en annen faktura for å gjøre endringer."
        : "Valgt fakturanummer er fullt. Fjern abonnement fra handlekurven for å endre konto.";

    const handleGoToAdditionalServices = () => {
        window.location.assign(
            `${PAGES.RETAILER.ORDER.ADDITIONAL_SERVICES.URL}?phoneNumber=${encodeURIComponent(sub.phoneNumber)}`,
        );
    };

    const getServiceSortPriority = (service: AdditionalService): number => {
        const { monthlyPrice } = service;
        if (monthlyPrice === undefined) return 1;
        if (typeof monthlyPrice === "number" && !Number.isNaN(monthlyPrice) && monthlyPrice > 0) return 0;
        return 2;
    };

    const additionalServices = [...(subscriptionInfo?.additionalServices ?? [])].sort(
        (a, b) => getServiceSortPriority(a) - getServiceSortPriority(b),
    );
    const activeServices = additionalServices.filter((service) => service.status === "ACTIVE");
    const otherServices = additionalServices.filter((service) => service.status !== "ACTIVE");
    const additionalServicesListId = `additional-services-${sub.phoneNumber}`;

    const renderAdditionalServiceRow = (service: AdditionalService) => {
        const fullServiceName = service.name ?? service.product;
        const priceText = formatServiceMonthlyPrice(service.monthlyPrice);
        const statusText = formatServiceStatus(service.status);
        const statusPriceText = [statusText, priceText].filter(Boolean).join(" ") || "\u2013";
        return (
            <React.Fragment key={service.product}>
                <dt title={fullServiceName}>{fullServiceName}</dt>
                <dd data-di-mask>{statusPriceText}</dd>
            </React.Fragment>
        );
    };

    const simSecretPlaceholder = "****";
    const pukSecretPlaceholder = "********";
    const currentPuk = subscriptionInfo?.mainSimcardPukCode || "Ikke oppgitt";
    const currentPin = subscriptionInfo?.mainSimcardInitialPin || "Ikke oppgitt";
    const nextPuk = hasChangedMainSim ? pukSecretPlaceholder : undefined;
    const nextPin = hasChangedMainSim ? simSecretPlaceholder : undefined;

    const displayPriceValue = isChanged && changedPrice !== undefined ? changedPrice : effectivePrice;
    const displayPrice = isChanged && changedPrice !== undefined ? changedPrice : effectivePrice || "n/a";
    const displayPriceSuffix = displayPriceValue ? ",-/md." : "";

    return (
        <RetailerSubscriptionAccordion
            id={sub.phoneNumber}
            isActive={isChanged ? undefined : isActive}
            hasSubscriptionChanged={!!hasSubscriptionChanged}
            existingOfferingCode={sub.offeringCode}
            newOfferingCode={changedSubscription?.code}
            hasSimChanged={hasChangedSim}
            hasUserChanged={hasUserChanged}
            hasServicesChanged={hasServicesChanged}
            isKeepExistingSubscription={!!changedSubscription?.keepExistingSubscription}
            changedFromName={isChanged ? sub.offeringName : undefined}
            title={getSubscriptionTitleWithBreakageFee(sub, isCustomerDataFetched)}
            name={displayName}
            price={displayPrice}
            priceSuffix={displayPriceSuffix}
            priceStriketrough={isChanged ? changedPriceStrikethrough : priceStriketrough}
            familyDiscount={familyDiscount}
            yearlyCommitmentLabel={yearlyCommitmentLabel}
            isSelected={isSelected}
            onSelectSubscription={() => {
                handleSelect(sub.phoneNumber);
            }}
            isSelectable={false}
        >
            <Card variant="beige-light" columns="1">
                <Card.Content>
                    <Flex direction="column" gap="300">
                        <div className="existing-sub-card__section">
                            <Flex
                                direction="row"
                                justify="between"
                                align="center"
                                mb="100"
                                className="existing-sub-card__section-actions"
                            >
                                <div className="existing-sub-card__section-header">
                                    <Icon name="simcard" size="sm" aria-hidden="true" focusable="false" />
                                    <Heading as="h3" variant="title-100">
                                        Abonnement
                                    </Heading>
                                </div>
                                {showActions && (
                                    <Box className="existing-sub-card__section-actions-box">
                                        {isChangeBlockedByAccountFull ? (
                                            <Tooltip>
                                                <Tooltip.Trigger asChild>
                                                    <Button
                                                        className="existing-sub-card__action-button"
                                                        variant="primary"
                                                        size="sm"
                                                        disabled
                                                        onClick={() =>
                                                            handleChangeSubscription(sub, "subscriptionChange")
                                                        }
                                                    >
                                                        Endre abonnement
                                                    </Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Content className="existing-sub-card__tooltip-content">
                                                    {accountFullTooltipMessage}
                                                </Tooltip.Content>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                className="existing-sub-card__action-button"
                                                variant="primary"
                                                size="sm"
                                                disabled={isCurrentlyAddingSubscription}
                                                onClick={() => handleChangeSubscription(sub, "subscriptionChange")}
                                            >
                                                Endre abonnement
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Flex>
                            <dl className="existing-sub-card__details">
                                {subscriptionInfo?.initialActivationDate && (
                                    <>
                                        <dt>Aktivt siden</dt>
                                        <dd data-di-mask>
                                            {moment(subscriptionInfo.initialActivationDate).format(
                                                DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."),
                                            )}
                                        </dd>
                                        {isCommitmentActive(subscriptionInfo.commitmentEndDate) && (
                                            <>
                                                <dt>Avtaletid</dt>
                                                <dd data-di-mask>
                                                    <Flex align="center" gapX="100">
                                                        {subscriptionInfo.commitmentStartDate && (
                                                            <span>
                                                                {moment(subscriptionInfo.commitmentStartDate).format(
                                                                    DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."),
                                                                )}
                                                            </span>
                                                        )}
                                                        {subscriptionInfo.commitmentStartDate &&
                                                            subscriptionInfo.commitmentEndDate && (
                                                                <Icon
                                                                    name="arrow-right"
                                                                    size="sm"
                                                                    aria-hidden="true"
                                                                    focusable="false"
                                                                />
                                                            )}
                                                        {subscriptionInfo.commitmentEndDate && (
                                                            <span>
                                                                {moment(subscriptionInfo.commitmentEndDate).format(
                                                                    DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."),
                                                                )}
                                                            </span>
                                                        )}
                                                    </Flex>
                                                </dd>
                                            </>
                                        )}
                                    </>
                                )}
                                {subscriptionInfo?.leaseInfo?.map((lease, index) => (
                                    <React.Fragment key={`${lease.model}-${index}`}>
                                        <dt>Delbetaling</dt>
                                        <dd data-di-mask>
                                            {lease.model}
                                            <br />
                                            Gjenstående:{" "}
                                            <strong>
                                                {lease.remainingInstallments} av {lease.totalInstallments}
                                            </strong>{" "}
                                            | Per md.: <strong>{lease.installmentAmount},-</strong> | Siste md.:{" "}
                                            <strong>{lease.lastInstallmentAmount},-</strong>
                                        </dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                            {showActions && isCurrentlyAddingSubscription && (
                                <Box mt="200">
                                    <HelperText>Fullfør pågående abonnement før man kan velge et annet.</HelperText>
                                </Box>
                            )}
                        </div>

                        <hr className="existing-sub-card__divider" />

                        <div className="existing-sub-card__section">
                            <Flex
                                direction="row"
                                justify="between"
                                align="center"
                                mb="100"
                                className="existing-sub-card__section-actions"
                            >
                                <div className="existing-sub-card__section-header">
                                    <Icon name="end-user" size="sm" aria-hidden="true" focusable="false" />
                                    <Heading as="h3" variant="title-100">
                                        Bruker
                                    </Heading>
                                </div>
                                {showActions && (
                                    <Box className="existing-sub-card__section-actions-box">
                                        {isChangeBlockedByAccountFull ? (
                                            <Tooltip>
                                                <Tooltip.Trigger asChild>
                                                    <Button
                                                        className="existing-sub-card__action-button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled
                                                        onClick={() => handleChangeSubscription(sub, "productUser")}
                                                    >
                                                        Endre bruker
                                                    </Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Content className="existing-sub-card__tooltip-content">
                                                    {accountFullTooltipMessage}
                                                </Tooltip.Content>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                className="existing-sub-card__action-button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleChangeSubscription(sub, "productUser")}
                                            >
                                                Endre bruker
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Flex>
                            <dl className="existing-sub-card__details">
                                <dt>Navn</dt>
                                <dd data-di-mask>{renderFieldDiff(currentUserName, nextUserName, true)}</dd>
                                <dt>Fødselsdato</dt>
                                <dd data-di-mask>{renderFieldDiff(currentBirthDate, nextBirthDate, true)}</dd>
                                <dt>E-post</dt>
                                <dd data-di-mask>{renderFieldDiff(currentEmail, nextEmail, true)}</dd>
                                <dt>Adresse</dt>
                                <dd data-di-mask>{renderFieldDiff(currentAddress, nextAddress, true)}</dd>
                            </dl>
                        </div>

                        <hr className="existing-sub-card__divider" />

                        <div className="existing-sub-card__section">
                            <Flex
                                direction="row"
                                justify="between"
                                align="center"
                                mb="100"
                                className="existing-sub-card__section-actions"
                            >
                                <div className="existing-sub-card__section-header">
                                    <Icon name="simcard" size="sm" aria-hidden="true" focusable="false" />
                                    <Heading as="h3" variant="title-100">
                                        SIM-kort
                                    </Heading>
                                </div>
                                {showActions && (
                                    <Box className="existing-sub-card__section-actions-box">
                                        {isChangeBlockedByAccountFull ? (
                                            <Tooltip>
                                                <Tooltip.Trigger asChild>
                                                    <Button
                                                        className="existing-sub-card__action-button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled
                                                        onClick={() => handleChangeSubscription(sub, "sim")}
                                                    >
                                                        Endre SIM-kort
                                                    </Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Content className="existing-sub-card__tooltip-content">
                                                    {accountFullTooltipMessage}
                                                </Tooltip.Content>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                className="existing-sub-card__action-button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleChangeSubscription(sub, "sim")}
                                            >
                                                Endre SIM-kort
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Flex>
                            <dl className="existing-sub-card__details">
                                <dt>Hoved-SIM</dt>
                                <dd data-di-mask>{renderFieldDiff(currentMainSim, nextMainSim, true)}</dd>
                                <dt>Type</dt>
                                <dd data-di-mask>{renderFieldDiff(currentSimType, nextSimType)}</dd>
                                <dt>PUK</dt>
                                <dd data-di-mask>{renderFieldDiff(currentPuk, nextPuk, true)}</dd>
                                <dt>PIN</dt>
                                <dd data-di-mask>{renderFieldDiff(currentPin, nextPin, true)}</dd>
                                {subscriptionInfo?.additionalSimcards &&
                                    subscriptionInfo.additionalSimcards.length > 0 && (
                                        <>
                                            {subscriptionInfo.additionalSimcards.map((sim) => {
                                                let simLabel: string;
                                                if (sim.simType === "TVILLINGSIM_KLOKKE") {
                                                    simLabel = "TvillingSIM (klokke)";
                                                } else if (sim.simType === "TVILLINGSIM") {
                                                    simLabel = "TvillingSIM";
                                                } else if (sim.simType === "DATASIM") {
                                                    simLabel = "DataSIM";
                                                } else {
                                                    simLabel = "Ekstra SIM";
                                                }
                                                const changedExtraSim = changedExtraSims.find(
                                                    (changed) => changed.productCode === sim.productCode,
                                                );
                                                const nextSimCardType = changedExtraSim
                                                    ? changedExtraSim.isEsim
                                                        ? "eSIM"
                                                        : "Fysisk SIM"
                                                    : undefined;
                                                const nextSimNumber = changedExtraSim
                                                    ? changedExtraSim.isEsim
                                                        ? "Nytt eSIM"
                                                        : formatSimNumber(changedExtraSim.existingSimNumber)
                                                    : undefined;
                                                return (
                                                    <React.Fragment key={`${sim.name}-${sim.simNumber ?? sim.simType}`}>
                                                        <dt className="existing-sub-card__additional-sim-spacing">
                                                            Tilleggstype
                                                        </dt>
                                                        <dd
                                                            data-di-mask
                                                            className="existing-sub-card__additional-sim-spacing"
                                                        >
                                                            {simLabel}
                                                        </dd>
                                                        <dt>SIM-korttype</dt>
                                                        <dd data-di-mask>
                                                            {renderFieldDiff(
                                                                sim.simCardType || "Ikke oppgitt",
                                                                nextSimCardType,
                                                            )}
                                                        </dd>
                                                        <dt>SIM-nummer</dt>
                                                        <dd data-di-mask>
                                                            {renderFieldDiff(
                                                                formatSimNumber(sim.simNumber),
                                                                nextSimNumber,
                                                                true,
                                                            )}
                                                        </dd>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </>
                                    )}
                            </dl>
                        </div>
                        {sub.switchAgreement && (
                            <>
                                <hr className="existing-sub-card__divider" />
                                <div className="existing-sub-card__section">
                                    <div className="existing-sub-card__section-header">
                                        <Icon name="reverse" size="sm" aria-hidden="true" focusable="false" />
                                        <Heading as="h3" variant="title-100">
                                            Svitsj-avtale
                                        </Heading>
                                    </div>
                                    <dl className="existing-sub-card__details">
                                        <dt>IMEI</dt>
                                        <dd data-di-mask>{sub.switchAgreement.imei}</dd>
                                        <dt>Modell</dt>
                                        <dd data-di-mask>{sub.switchAgreement.model}</dd>
                                        <dt>Gjenstående obligatoriske avdrag</dt>
                                        <dd data-di-mask>{sub.switchAgreement.remainingMandatoryInstallments}</dd>
                                        {sub.switchAgreement.totalRemainingCost && (
                                            <>
                                                <dt>Total gjenstående kostnad</dt>
                                                <dd data-di-mask>
                                                    {sub.switchAgreement.totalRemainingCost.amount.toLocaleString(
                                                        "nb-NO",
                                                    )}{" "}
                                                    {sub.switchAgreement.totalRemainingCost.currency}
                                                </dd>
                                            </>
                                        )}
                                        {sub.switchAgreement.costToReplace && (
                                            <>
                                                <dt>Kostnad for bytte</dt>
                                                <dd data-di-mask>
                                                    {sub.switchAgreement.costToReplace.amount.toLocaleString("nb-NO")}{" "}
                                                    {sub.switchAgreement.costToReplace.currency}
                                                </dd>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            </>
                        )}

                        <hr className="existing-sub-card__divider" />

                        <div className="existing-sub-card__section">
                            <Flex
                                direction="row"
                                justify="between"
                                align="center"
                                mb="100"
                                className="existing-sub-card__section-actions"
                            >
                                <div className="existing-sub-card__section-header">
                                    <Icon name="services" size="sm" aria-hidden="true" focusable="false" />
                                    <Heading as="h3" variant="title-100">
                                        Tilleggstjenester
                                    </Heading>
                                </div>
                                {showActions && (
                                    <Box className="existing-sub-card__section-actions-box">
                                        {isChangeBlockedByAccountFull ? (
                                            <Tooltip>
                                                <Tooltip.Trigger asChild>
                                                    <Button
                                                        className="existing-sub-card__action-button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled
                                                        onClick={handleGoToAdditionalServices}
                                                    >
                                                        Endre tjenester
                                                    </Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Content className="existing-sub-card__tooltip-content">
                                                    {accountFullTooltipMessage}
                                                </Tooltip.Content>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                className="existing-sub-card__action-button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={handleGoToAdditionalServices}
                                            >
                                                Endre tjenester
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Flex>
                            {activeServices.length === 0 && otherServices.length === 0 && (
                                <Text as="p" variant="additional-100">
                                    <i>Ingen tilleggstjenester registrert</i>
                                </Text>
                            )}
                            {(activeServices.length > 0 || (isServicesExpanded && otherServices.length > 0)) && (
                                <dl
                                    id={additionalServicesListId}
                                    className="existing-sub-card__details existing-sub-card__details--services"
                                >
                                    {activeServices.map(renderAdditionalServiceRow)}
                                    {isServicesExpanded && otherServices.map(renderAdditionalServiceRow)}
                                </dl>
                            )}
                            {otherServices.length > 0 && (
                                <Flex justify="center" mt="300">
                                    <Button
                                        type="button"
                                        variant="text-underline"
                                        aria-expanded={isServicesExpanded}
                                        aria-controls={additionalServicesListId}
                                        onClick={() => setIsServicesExpanded(!isServicesExpanded)}
                                    >
                                        {isServicesExpanded ? "Skjul" : `Vis ${otherServices.length} flere tjenester`}
                                        <Icon
                                            name={isServicesExpanded ? "chevron-up" : "chevron-down"}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    </Button>
                                </Flex>
                            )}
                        </div>
                    </Flex>
                </Card.Content>
            </Card>
        </RetailerSubscriptionAccordion>
    );
};

export default ExistingSubscriptionCard;
